import { useEffect, useRef } from 'react';
import { matchPath } from 'react-router-dom';

const manifestSelector = 'link[rel="manifest"]';

const getTenantBasePath = (pathname) => {
  const match = matchPath(pathname, {
    path: '/:organizationId/:propertyId',
    exact: false,
    strict: false,
  });

  const organizationId = match?.params?.organizationId;
  const propertyId = match?.params?.propertyId;

  if (!organizationId?.length || !propertyId?.length) {
    return null;
  }

  return `/${organizationId}/${propertyId}`;
};

const getStaticManifestUrl = () => `${process.env.PUBLIC_URL}/manifest.json`;

const toAbsoluteUrl = (value, baseUrl = window.location.origin) => {
  if (!value) {
    return value;
  }

  try {
    return new URL(value).toString();
  } catch (error) {
    return new URL(value, baseUrl).toString();
  }
};

const getManifestLink = () => document.querySelector(manifestSelector);

const setManifestHref = (href) => {
  const manifestLink = getManifestLink();

  if (manifestLink) {
    manifestLink.href = href;
  }
};

const revokeObjectUrl = (objectUrlRef) => {
  if (objectUrlRef.current) {
    URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = null;
  }
};

const useInstallManifestSync = (pathname) => {
  const objectUrlRef = useRef(null);
  const tenantBasePath = getTenantBasePath(pathname);

  useEffect(() => {
    let isCancelled = false;
    const staticManifestUrl = getStaticManifestUrl();
    const absoluteManifestUrl = toAbsoluteUrl(
      staticManifestUrl,
      window.location.origin,
    );

    if (!tenantBasePath) {
      revokeObjectUrl(objectUrlRef);
      setManifestHref(staticManifestUrl);
      return undefined;
    }

    const syncManifest = async () => {
      const response = await fetch(staticManifestUrl, { cache: 'no-store' });

      if (!response.ok) {
        revokeObjectUrl(objectUrlRef);
        setManifestHref(staticManifestUrl);
        return;
      }

      const manifest = await response.json();
      manifest.id = tenantBasePath;
      manifest.scope = toAbsoluteUrl(tenantBasePath, window.location.origin);
      manifest.start_url = toAbsoluteUrl(
        tenantBasePath,
        window.location.origin,
      );
      manifest.icons = (manifest.icons || []).map((icon) => ({
        ...icon,
        src: toAbsoluteUrl(icon.src, absoluteManifestUrl),
      }));
      manifest.screenshots = (manifest.screenshots || []).map((screenshot) => ({
        ...screenshot,
        src: toAbsoluteUrl(screenshot.src, absoluteManifestUrl),
      }));

      const manifestBlob = new Blob([JSON.stringify(manifest)], {
        type: 'application/manifest+json',
      });
      const objectUrl = URL.createObjectURL(manifestBlob);

      if (isCancelled) {
        URL.revokeObjectURL(objectUrl);
        return;
      }

      revokeObjectUrl(objectUrlRef);
      objectUrlRef.current = objectUrl;
      setManifestHref(objectUrl);
    };

    syncManifest().catch(() => {
      revokeObjectUrl(objectUrlRef);
      setManifestHref(staticManifestUrl);
    });

    return () => {
      isCancelled = true;
    };
  }, [tenantBasePath]);

  useEffect(
    () => () => {
      revokeObjectUrl(objectUrlRef);
    },
    [],
  );
};

export default useInstallManifestSync;
